import { useState, useRef } from 'react';
import SEO from '../components/SEO';
import ContactBox from '../components/ContactBox';
import Breadcrumbs from '../components/Breadcrumbs';
import { createBreadcrumbSchema } from '../lib/schema';
import { pagesData } from '../data';
import { getOptimizedImageUrl } from '../lib/utils';
import { 
  Phone, 
  Mail, 
  CheckCircle2, 
  Shield, 
  Award, 
  Users, 
  Briefcase, 
  Upload, 
  X, 
  FileText, 
  ChevronRight, 
  AlertCircle 
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Careers() {
  const page = pagesData["ledige-stillinger"];
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  if (!page) return <div className="py-40 text-center">Siden blev ikke fundet.</div>;

  // File validation constraints
  const MAX_FILE_COUNT = 5;
  const MAX_FILE_SIZE_MB = 10;
  const MAX_TOTAL_SIZE_MB = 20;
  const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

  const handleFileChange = (e) => {
    setFileError('');
    const selectedFiles = Array.from(e.target.files || []);
    
    if (files.length + selectedFiles.length > MAX_FILE_COUNT) {
      setFileError(`Du kan højst uploade ${MAX_FILE_COUNT} filer i alt.`);
      return;
    }

    const newFiles = [...files];
    let totalSize = files.reduce((sum, f) => sum + f.size, 0);

    for (const file of selectedFiles) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setFileError(`Filtypen "${file.name}" understøttes ikke. Tilladte formater: PDF, PNG, JPG, GIF, WEBP.`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileError(`Filen "${file.name}" er for stor. Maksimal filstørrelse er ${MAX_FILE_SIZE_MB} MB.`);
        return;
      }
      totalSize += file.size;
      if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
        setFileError(`Den samlede størrelse af alle filer må ikke overstige ${MAX_TOTAL_SIZE_MB} MB.`);
        return;
      }
      newFiles.push(file);
    }

    setFiles(newFiles);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset input to allow selecting same file again
    }
  };

  const removeFile = (indexToRemove) => {
    setFiles(files.filter((_, idx) => idx !== indexToRemove));
    setFileError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setFileError('');

    const form = e.target;
    const formData = new FormData(form);

    // Append files to FormData
    // The server captures files from every field, so we can append them under 'cv'
    // Clear any existing 'cv' entries from the default form binding first
    formData.delete('cv');
    files.forEach((file) => {
      formData.append('cv', file);
    });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData, // Send as multipart/form-data, browser sets boundary automatically
      });

      if (response.ok) {
        setStatus('success');
        setFiles([]);
        form.reset();
      } else {
        const errData = await response.json().catch(() => ({}));
        setStatus('error');
        setFileError(errData.message || 'Der skete en fejl under indsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      setStatus('error');
      setFileError('Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    }
  };

  // Typical job profiles we look for
  const jobProfiles = [
    {
      title: "Anlægsstruktør / Brolægger",
      description: "Vi søger brolæggere og anlægsstruktører med erfaring inden for belægning, flisearbejde og etablering af indkørsler og terrasser.",
      requirements: [
        "Erfaring med brolægning og afretning",
        "Kendskab til nivellering og læsning af tegninger",
        "Kørekort B (gerne BE/trailerkort)",
        "Mødestabil og kvalitetsbevidst"
      ]
    },
    {
      title: "Kloakrørlægger",
      description: "Vi søger dygtige folk til kloakarbejde, herunder kloakseparering, omfangsdræn og reparationer under vores autoriserede kloakmester.",
      requirements: [
        "Gennemført rørlæggeruddannelse (trin 1 + 2)",
        "Erfaring med kloakseparering og nedsivning",
        "Evne til at arbejde selvstændigt ud fra tegninger",
        "Kørekort B"
      ]
    },
    {
      title: "Maskinfører / Entreprenørmedarbejder",
      description: "Vi søger alsidige maskinførere til betjening af minigravere, gravemaskiner og andet entreprenørmateriel på vores projekter.",
      requirements: [
        "Erfaring med kørsel af gravemaskiner (1-15 tons)",
        "Erfaring med jord- og gravearbejde",
        "Kørekort B (gerne BE eller maskinførerbevis)",
        "Fleksibel og klar på fysisk arbejde ved behov"
      ]
    }
  ];

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Forside', url: 'https://janhedegaard.dk/' },
    { name: 'Ledige stillinger', url: 'https://janhedegaard.dk/ledige-stillinger' }
  ]);

  return (
    <>
      <SEO 
        title={page.title} 
        description={page.meta?.ogDescription || "Bliv en del af holdet hos Entreprenør Jan Hedegaard ApS. Vi søger altid dygtige og engagerede kolleger."}
        image={page.meta?.ogImage}
        schemaMarkup={breadcrumbSchema}
      />

      {/* Hero */}
      <section className="relative min-h-[480px] py-24 md:py-28 flex items-center bg-stone-900">
        <div className="absolute inset-0">
          <img 
            src={getOptimizedImageUrl(page.meta?.ogImage || "https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/5fc0604f-f47c-4a77-b4ac-e33a53e50bd6.jpg", 1600)} 
            alt={page.headline} 
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/70 via-stone-900/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-16 pt-[40px]">
          <div className="mb-6">
            <Breadcrumbs items={[{ label: 'Ledige stillinger', href: '/ledige-stillinger' }]} isDark={true} />
          </div>
          <span className="inline-block bg-orange-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-full mb-4">
            Karriere & Job
          </span>
          <h1 className="text-[1.8rem] md:text-6xl font-bold text-white drop-shadow-lg max-w-4xl leading-[1.15]">
            {page.headline}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-stone-300 max-w-2xl drop-shadow">
            Vi ansætter desværre ikke i øjeblikket, men du er altid velkommen til at sende en uopfordret ansøgning.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            
            {/* Left Column: Info & Quality Statement */}
            <div className="space-y-12">
              {page.sections && page.sections.map((section, sIdx) => (
                <div 
                  key={sIdx} 
                  className={`${
                    sIdx === 0 
                      ? 'border-l-4 border-orange-600 pl-6 space-y-6' 
                      : 'bg-stone-50 p-8 rounded-2xl border border-stone-100 space-y-6'
                  }`}
                >
                  <h2 className={`${sIdx === 0 ? 'text-3xl font-bold text-stone-900' : 'text-2xl font-bold text-stone-900 flex items-center gap-3'}`}>
                    {sIdx === 1 && <Award className="w-6 h-6 text-orange-600" />}
                    {section.title}
                  </h2>
                  <div className="space-y-4 text-stone-600 leading-relaxed">
                    {section.paragraphs.map((para, pIdx) => {
                      if (para.includes('jan@janhedegaard.dk')) {
                        const parts = para.split('jan@janhedegaard.dk');
                        return (
                          <p key={pIdx} className={sIdx === 0 ? 'text-lg' : 'text-base'}>
                            {parts[0]}
                            <a href="mailto:jan@janhedegaard.dk" className="text-orange-600 hover:underline font-semibold">
                              jan@janhedegaard.dk
                            </a>
                            {parts[1]}
                          </p>
                        );
                      }
                      return (
                        <p key={pIdx} className={sIdx === 0 ? 'text-lg' : 'text-base'}>
                          {para}
                        </p>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column: Application Form */}
            <div className="bg-stone-50 border border-stone-200 rounded-2xl p-8 lg:sticky lg:top-28 shadow-sm">
              <h3 className="text-2xl font-bold text-stone-900 mb-2">Send en uopfordret ansøgning</h3>
              <p className="text-stone-600 text-sm mb-8">
                Udfyld formularen nedenfor og vedhæft dit CV eller en kort beskrivelse af dig selv. Vi behandler alle henvendelser fortroligt.
              </p>

              {status === 'success' ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-8 rounded-xl text-center space-y-4 animate-fadeIn">
                  <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h4 className="text-xl font-bold">Ansøgning modtaget!</h4>
                  <p className="text-sm leading-relaxed">
                    Mange tak for din interesse i at arbejde hos os. Vi har modtaget dine oplysninger og dit CV, og vi vil gennemgå det hurtigst muligt. Hvis der opstår en mulighed, kontakter vi dig med det samme.
                  </p>
                  <button 
                    onClick={() => setStatus('idle')}
                    className="mt-4 inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-900 font-bold text-sm transition-colors"
                  >
                    Send en ny ansøgning
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Antispam Honeypot */}
                  <div className="sr-only absolute -top-96 -left-96">
                    <label htmlFor="website_url">Honeypot - udfyld ikke denne</label>
                    <input 
                      type="text" 
                      id="website_url" 
                      name="website_url" 
                      tabIndex="-1" 
                      autoComplete="off" 
                    />
                  </div>

                  {/* Hidden Subject for Webform */}
                  <input type="hidden" name="subject" value="Uopfordret ansøgning via hjemmeside" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                        Navn <span className="text-orange-600">*</span>
                      </label>
                      <input 
                        type="text" 
                        name="name" 
                        required 
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-3 rounded-lg border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                        Telefon <span className="text-orange-600">*</span>
                      </label>
                      <input 
                        type="tel" 
                        name="phone" 
                        required 
                        placeholder="Dit telefonnummer"
                        className="w-full px-4 py-3 rounded-lg border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                      E-mail <span className="text-orange-600">*</span>
                    </label>
                    <input 
                      type="email" 
                      name="email" 
                      required 
                      placeholder="Din e-mailadresse"
                      className="w-full px-4 py-3 rounded-lg border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                      Hvilken stilling søger du? <span className="text-orange-600">*</span>
                    </label>
                    <select 
                      name="position" 
                      required
                      className="w-full px-4 py-3 rounded-lg border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                    >
                      <option value="">Vælg en kategori...</option>
                      <option value="Anlægsstruktør / Brolægger">Anlægsstruktør / Brolægger</option>
                      <option value="Kloakrørlægger">Kloakrørlægger</option>
                      <option value="Maskinfører">Maskinfører</option>
                      <option value="Ufaglært entreprenørmedarbejder">Ufaglært entreprenørmedarbejder</option>
                      <option value="Andet">Andet (specificer i beskeden)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                      Kort beskrivelse af dig selv <span className="text-orange-600">*</span>
                    </label>
                    <textarea 
                      name="message" 
                      required 
                      rows={4}
                      placeholder="Fortæl os kort om din erfaring, hvorfor du søger, og hvad du kan bidrage med..."
                      className="w-full px-4 py-3 rounded-lg border border-stone-300 bg-white text-stone-900 text-sm focus:outline-none focus:border-orange-500 transition-colors resize-none"
                    />
                  </div>

                  {/* File Upload Area */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                      Vedhæft CV & Ansøgning (Valgfrit)
                    </label>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-stone-300 hover:border-orange-500 bg-white rounded-xl p-6 text-center cursor-pointer transition-colors"
                    >
                      <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-stone-700">Klik for at vælge filer</p>
                      <p className="text-xs text-stone-500 mt-1">PDF, PNG, JPG, WEBP (Maks. 5 filer, 10 MB pr. fil)</p>
                      <input 
                        type="file"
                        ref={fileInputRef}
                        name="cv"
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </div>

                    {/* File List */}
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {files.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg border border-stone-200 text-xs">
                            <div className="flex items-center gap-2 text-stone-700 truncate">
                              <FileText className="w-4 h-4 text-orange-600 shrink-0" />
                              <span className="truncate font-medium">{file.name}</span>
                              <span className="text-stone-400 text-[10px] shrink-0">
                                ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                              </span>
                            </div>
                            <button 
                              type="button"
                              onClick={() => removeFile(idx)}
                              className="text-stone-400 hover:text-red-500 p-1 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Error Message */}
                  {fileError && (
                    <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg flex items-start gap-3 text-sm">
                      <AlertCircle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
                      <span>{fileError}</span>
                    </div>
                  )}

                  <button 
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full bg-orange-600 text-white py-4 px-6 rounded-xl font-bold hover:bg-orange-700 transition-all shadow-md hover:shadow-lg hover:shadow-orange-600/20 disabled:bg-stone-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {status === 'submitting' ? (
                      <>
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Sender ansøgning...
                      </>
                    ) : (
                      'Send uopfordret ansøgning'
                    )}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactBox 
            title="Har du spørgsmål om job eller karriere?"
            subtitle="Du er altid velkommen til at ringe til Jan på 22 45 85 67 for en uformel snak om mulighederne."
          />
        </div>
      </section>
    </>
  );
}
